
import {
  defineComponent,
  defineAsyncComponent,
  ref,
  Ref,
  computed,
  watch,
  onMounted,
  nextTick,
  inject,
  onUpdated,
} from "vue";
import { fabric } from "fabric";
import { store } from "@/store";

import ScrollBar from "@/components/Canvas/ScrollBar.vue";
const AsyncLeftRuler = defineAsyncComponent(() =>
  import("@/components/Canvas/Rulers/LeftRuler.vue" /* webpackChunkName: "leftRuler" */)
);
const AsyncTopRuler = defineAsyncComponent(() =>
  import("@/components/Canvas/Rulers/TopRuler.vue" /* webpackChunkName: "topRuler" */)
);

import { undoActionStack } from "@/canvas-api/ActionStack";
import { moveUserObjectToBack, arrangeObjectStack } from "@/canvas-api/ArrangeObjectStack";
import {
  canvasRef,
  canvasWrap,
  canvas,
  mountMainCanvas,
  canvasReactive,
} from "@/canvas-api/Canvas";
import { handleGridLines } from "@/canvas-api/GridLines";
import { hideIcons, iconAction, selectedObjectId } from "@/canvas-api/ObjectIcons";
import {
  centerCanvasViewPort,
  centerAndSizeScrollers,
  setHorizScrollerMaxWidth,
  setVertScrollerMaxHeight,
} from "@/canvas-api/ViewPort";
import { zoomValue, setZoomValue } from "@/canvas-api/Zoom";

import { ActionTypes } from "@/store/actions";
import { ClientDimensions } from "@/types";

export default defineComponent({
  name: "Canvas",
  components: {
    ScrollBar,
    AsyncLeftRuler,
    AsyncTopRuler,
  },
  setup() {
    const isMobile = computed<boolean>(() => store.getters.isMobile);
    const showRulers = computed<boolean>(() => store.getters.settingsState.rulers);
    const isRotated = computed<boolean>(() =>
      store.getters.canvasOrientation === "rotated" ? true : false
    );
    const currentActiveObject = computed(() => store.getters.currentActiveObject);
    const mountingLabel = computed<number>(() => store.getters.mountingLabel);
    const showGrid = computed<boolean>(() => store.getters.settingsState.grid);

    const canvasWrapWidth = ref(0);
    const canvasWrapHeight = ref(0);

    onMounted(async () => {
      if (isMobile.value) {
        store.dispatch(ActionTypes.SET_SETTINGS_STATE, {
          guidelines: true,
          rulers: false,
          grid: false,
        });
      }

      if (canvasWrap.value) {
        canvasWrapWidth.value = canvasWrap.value.clientWidth;
        canvasWrapHeight.value = canvasWrap.value.clientHeight;
      }
    });

    onUpdated(async () => {
      if (mountingLabel.value && canvasRef.value) {
        await mountMainCanvas(canvasWrapWidth.value, canvasWrapHeight.value);

        buildGuidelinesModalBtn();

        canvas.value.renderAll();

        store.dispatch(ActionTypes.SET_MOUNTING_LABEL, false);
      }
    });

    /* RESIZE CANVAS ON WINDOW RESIZE */
    const canvasWrapClientWidth = inject<Ref<number>>("canvasWrapClientWidth", ref(0));
    const canvasWrapClientHeight = inject<Ref<number>>("canvasWrapClientHeight", ref(0));
    const isResizing = inject<Ref<boolean>>("isResizing", ref(false));

    watch(isResizing, (isResizing: boolean) => {
      if (!isResizing) {
        const canvasCenterBefore = canvas.value.getCenter();

        canvasWrapWidth.value = canvasWrapClientWidth.value;
        canvasWrapHeight.value = canvasWrapClientHeight.value;
        canvas.value.setWidth(canvasWrapClientWidth.value);
        canvas.value.setHeight(canvasWrapClientHeight.value);

        const canvasCenterAfter = canvas.value.getCenter();
        const diffLeft = canvasCenterAfter.left - canvasCenterBefore.left;
        const diffTop = canvasCenterAfter.top - canvasCenterBefore.top;

        canvas.value._objects.forEach((obj) => {
          const center = obj.getCenterPoint();

          obj.set({
            left: obj.left ? obj.left + diffLeft : center.x + diffLeft,
            top: obj.top ? obj.top + diffTop : center.y + diffTop,
          });
        });

        setZoomValue(100);
        setHorizScrollerMaxWidth();
        setVertScrollerMaxHeight();
        centerAndSizeScrollers();
        centerCanvasViewPort();

        if (showGrid.value) {
          const snapLines = canvas.value._objects.find((obj) => obj.name === "snapLineGroup");
          if (snapLines) canvas.value.remove(snapLines);
          handleGridLines(canvas.value);
        }

        const payload: ClientDimensions = {
          width: document.body.clientWidth,
          height: document.body.clientHeight,
        };

        store.dispatch(ActionTypes.SET_CLIENT_DIMENSIONS, payload);
      }
    });

    /* GUIDELINES MODAL START */
    const modalBtnPadding = 10;
    const buildGuidelinesModalBtn = () => {
      const bleed = canvas.value._objects.find((obj) => obj.name === "bleedLine");

      if (bleed) {
        const bleedCoords = bleed.calcCoords(true);
        const rectWidth = 60;
        const rectHeight = 10;

        const shadow = new fabric.Shadow({
          color: "#66CFF3",
          offsetX: 0.25,
          offsetY: 0.25,
          blur: 0.25,
        });

        const btnRect = new fabric.Rect({
          left: bleedCoords.br.x - (rectWidth + modalBtnPadding) / 2,
          top: bleedCoords.br.y + (rectHeight + modalBtnPadding) / 2 + modalBtnPadding,
          originX: "center",
          originY: "center",
          fill: "#FFF",
          width: rectWidth + modalBtnPadding,
          height: rectHeight + modalBtnPadding,
          rx: 2.5,
          ry: 2.5,
          stroke: "#66CFF3",
          strokeWidth: 0,
          shadow,
        });

        fabric.loadSVGFromURL(require("@/assets/icons/question.svg"), function(results, options) {
          const btnRectCoords = btnRect.calcCoords(true);

          const question = fabric.util.groupSVGElements(results, options);

          question.set({
            originX: "center",
            originY: "center",
          });

          question.scaleToHeight(rectHeight);

          question.set({
            left: btnRectCoords.tr.x - question.getScaledWidth() / 2 - 5,
            top: (btnRectCoords.tr.y + btnRectCoords.br.y) / 2,
          });

          const btnTxt = new fabric.Text("Guidelines", {
            fontFamily: "proxima-nova",
            fontSize: 8,
            opacity: 1,
            fill: "#000",
            originX: "center",
            originY: "center",
          });

          btnTxt.scaleToHeight(rectHeight);

          btnTxt.set({
            left: btnRectCoords.tl.x + btnTxt.getScaledWidth() / 2 + 5,
            top: (btnRectCoords.tl.y + btnRectCoords.bl.y) / 2,
          });

          const btnGroup = new fabric.Group([btnRect, btnTxt, question], {
            selectable: false,
            hoverCursor: "pointer",
            originX: "center",
            originY: "center",
            name: "guidelinesBtn",
          });

          btnGroup.on("mousedown", (): void => {
            const btnGroupCP = btnGroup.getCenterPoint();

            btnGroup.set({
              left: btnGroupCP.x + 1,
              top: btnGroupCP.y + 1,
            });

            store.dispatch(ActionTypes.SET_MODAL, "guideLines");
          });

          btnGroup.on("mouseup", (): void => {
            const btnGroupCP = btnGroup.getCenterPoint();

            btnGroup.set({
              left: btnGroupCP.x - 1,
              top: btnGroupCP.y - 1,
            });

            canvas.value.renderAll();
          });

          btnGroup.on("mouseover", (): void => {
            btnRect.set({
              strokeWidth: 0.5,
            });

            canvas.value.renderAll();
          });

          btnGroup.on("mouseout", (): void => {
            btnRect.set({
              strokeWidth: 0,
            });

            canvas.value.renderAll();
          });

          canvas.value.add(btnGroup);

          if (isRotated.value) {
            btnGroup.set({
              left: bleedCoords.tr.x - btnGroup.getScaledWidth() / 2,
              top: bleedCoords.tr.y + btnGroup.getScaledHeight() + modalBtnPadding,
            });
          } else {
            btnGroup.set({
              left: bleedCoords.br.x - btnGroup.getScaledWidth() / 2,
              top: bleedCoords.br.y + btnGroup.getScaledHeight() + modalBtnPadding,
            });
          }
        });
      }
    };

    const updateGuidelinesModalBtn = () => {
      const guidelinesBtn = canvas.value._objects.find((obj) => obj.name === "guidelinesBtn");
      const bleed = canvas.value._objects.find((obj) => obj.name === "bleedLine");

      if (guidelinesBtn && bleed) {
        const bleedCoords = bleed.calcCoords(true);

        if (isRotated.value) {
          guidelinesBtn.set({
            left: bleedCoords.tr.x - guidelinesBtn.getScaledWidth() / 2,
            top: bleedCoords.tr.y + guidelinesBtn.getScaledHeight() + modalBtnPadding,
          });
        } else {
          guidelinesBtn.set({
            left: bleedCoords.br.x - guidelinesBtn.getScaledWidth() / 2,
            top: bleedCoords.br.y + guidelinesBtn.getScaledHeight() + modalBtnPadding,
          });
        }
      }
    };

    Object.assign(canvasReactive, { updateGuidelinesModalBtn });
    /* GUIDELINES MODAL END */

    /* OBJECT ICONS CANVAS LOGIC */
    const stackPlacementRef = ref<HTMLDivElement>();
    const showStackPlacement = ref(false);
    const setShowStackPlacement = (value: boolean) => {
      showStackPlacement.value = value;
    };

    Object.assign(canvasReactive, { setShowStackPlacement });

    const iconLabelPlacementRef = ref<HTMLDivElement>();
    const iconLabel = ref("");

    watch(iconAction, async (newAction: string) => {
      if (newAction.length) {
        switch (newAction) {
          case "lock": {
            const selectedObject = canvas.value.getObjects().find((obj) => {
              if (obj.id && obj.id === selectedObjectId.value) {
                return obj;
              }
            });

            if (selectedObject) {
              const lockMovementX = !selectedObject.lockMovementX;
              const lockMovementY = !selectedObject.lockMovementX;
              const lockRotation = !selectedObject.lockMovementX;
              const lockUniScaling = !selectedObject.lockMovementX;
              const lockScalingX = !selectedObject.lockMovementX;
              const lockScalingY = !selectedObject.lockMovementX;

              selectedObject.set({
                lockMovementX,
                lockMovementY,
                lockRotation,
                lockUniScaling,
                lockScalingX,
                lockScalingY,
              });

              if (iconLabel.value.length) {
                if (iconLabel.value === "Lock") {
                  iconLabel.value = "Unlock";
                } else {
                  iconLabel.value = "Lock";
                }
              }

              //When clicking one of the object icons it fires a deselect that cannot be ignored
              //Reset selected to be current on the following tick so the deselection finishes firing
              await nextTick();
              canvas.value.setActiveObject(selectedObject);
              store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, selectedObject);
            }
            break;
          }
          case "delete": {
            const selectedObject = canvas.value.getObjects().find((obj) => {
              if (obj.id && obj.id === selectedObjectId.value) {
                return obj;
              }
            });

            if (selectedObject) {
              iconLabel.value = "";

              hideIcons();

              undoActionStack.push({
                id: selectedObject.id as number,
                type: "delete",
                object: fabric.util.object.clone(selectedObject),
              });

              canvas.value.remove(selectedObject).renderAll();
            }
            break;
          }
          case "arrange": {
            const canvasObjects = canvas.value.getObjects() as fabric.Object[];

            const selectedObject = canvas.value.getObjects().find((obj) => {
              if (obj.id && obj.id === selectedObjectId.value) {
                return obj;
              }
            });

            const arrangeIcon = canvasObjects.find((obj) => {
              if (obj.name && obj.name === "objectIconArrange") {
                return obj;
              }
            });

            if (selectedObject && arrangeIcon && stackPlacementRef.value) {
              const arrangeCoords = arrangeIcon.calcCoords();

              const left = arrangeCoords.mb.x;
              const top = arrangeCoords.mb.y;

              stackPlacementRef.value.style.left = `${left}px`;
              stackPlacementRef.value.style.top = `${top + 5}px`;

              showStackPlacement.value = !showStackPlacement.value;

              //When clicking one of the object icons it fires a deselect that cannot be ignored
              //Reset selected to be current on the following tick so the deselection finishes firing
              await nextTick();
              canvas.value.setActiveObject(selectedObject);
              store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, selectedObject);
            }

            break;
          }
          case "hide_icon": {
            iconLabel.value = "";
            break;
          }
          default:
            if (newAction.startsWith("show_")) {
              iconLabel.value = newAction.split("show_")[1];
              iconLabel.value = iconLabel.value.charAt(0).toUpperCase() + iconLabel.value.slice(1);

              const canvasObjects = canvas.value.getObjects() as fabric.Object[];
              const shownIcon = canvasObjects.find((obj) => {
                if (obj.name && obj.name === `objectIcon${iconLabel.value}`) return obj;
              });

              if (shownIcon && iconLabelPlacementRef.value) {
                const iconCoords = shownIcon.oCoords || shownIcon.calcCoords();
                const outerBounds = iconLabelPlacementRef.value.getBoundingClientRect();

                if (currentActiveObject.value.lockMovementX && iconLabel.value === "Lock")
                  iconLabel.value = "Unlock";

                // prettier-ignore
                const left = iconCoords.tl.x + (shownIcon.getScaledWidth() * (zoomValue.value / 100)) / 2 - outerBounds.width / 2;
                const top = iconCoords.tl.y - (outerBounds.height + 2);

                iconLabelPlacementRef.value.style.left = `${left}px`;
                iconLabelPlacementRef.value.style.top = `${top}px`;
              }
            }
            break;
        }

        //clear action so watcher updates if same action is selected
        iconAction.value = "";
      }
    });

    const arrangeStack = (command: string) => {
      const activeObjId = currentActiveObject.value.id;
      const activeObj = canvas.value._objects.find((obj) => {
        if (obj.id && obj.id === activeObjId) {
          return obj;
        }
      });

      if (activeObj) {
        switch (command) {
          case "bring-to-front": {
            canvas.value.bringToFront(activeObj);
            break;
          }
          case "bring-forward": {
            canvas.value.bringForward(activeObj);
            break;
          }
          case "send-backwards": {
            canvas.value.sendBackwards(activeObj);
            break;
          }
          case "send-to-back": {
            moveUserObjectToBack(canvas.value, activeObj);
            break;
          }
        }
      }

      arrangeObjectStack(canvas.value);
      showStackPlacement.value = false;
    };
    /* OBJECT ICONS CANVAS LOGIC */

    return {
      canvasRef,
      canvas,
      canvasWrapWidth,
      canvasWrapHeight,
      showRulers,
      stackPlacementRef,
      showStackPlacement,
      iconLabel,
      iconLabelPlacementRef,
      arrangeStack,
    };
  },
});
